/**
 * Helpers for reading a browser File into memory before uploading it.
 *
 * Browsers read a selected/dropped File lazily, at the moment the request body
 * is sent. Chromium validates that the underlying file has not changed since
 * selection and aborts the request otherwise (net::ERR_UPLOAD_FILE_CHANGED),
 * which surfaces to JavaScript as a generic network error with status 0 —
 * indistinguishable from a real connectivity failure. The most common trigger
 * is a file that is still open in another program (e.g. an .xlsx open in
 * Excel), where retrying with the same File handle can never succeed.
 */

/**
 * Files up to this size are copied into memory before upload so the request
 * body stays stable even if the file on disk changes. Larger files are
 * uploaded from the handle directly to bound memory usage.
 */
export const MAX_FILE_SNAPSHOT_BYTES = 50 * 1024 * 1024;

export class FileNotReadableError extends Error {
  readonly fileName: string;

  constructor(fileName: string, options?: { cause?: unknown }) {
    super(
      `Could not read "${fileName}". The file may be open in another program or was changed after it was selected — close it and try uploading again.`,
      options,
    );
    this.name = 'FileNotReadableError';
    this.fileName = fileName;
  }
}

/**
 * Copies the file bytes into memory so the upload (and any retries) send a
 * stable body even if the file on disk is locked or modified afterwards.
 * Files larger than MAX_FILE_SNAPSHOT_BYTES are returned as-is.
 *
 * @throws FileNotReadableError when the file cannot be read.
 */
export async function snapshotFileForUpload(file: File): Promise<Blob> {
  if (file.size > MAX_FILE_SNAPSHOT_BYTES) {
    return file;
  }
  try {
    return new Blob([await file.arrayBuffer()], { type: file.type });
  } catch (error) {
    throw new FileNotReadableError(file.name, { cause: error });
  }
}

/**
 * Cheap read probe to tell a locally unreadable/changed file apart from a
 * genuine network failure after an upload rejects with no HTTP response.
 */
export async function isFileReadable(file: File): Promise<boolean> {
  try {
    await file.slice(0, 1).arrayBuffer();
    return true;
  } catch {
    return false;
  }
}
